import events from "add-event-listener";
import PropTypes from "prop-types";
import * as React from "react";
import { getScrollWidth } from "react-ui";

import { smoothScrollBy } from "../../../../../Engine/utils";
import { PAGE_PADDING } from "../../../../constants";
import { themed } from "../../../../Internals/ThemeProvider/ThemedWorkaround";
import { isChildOfType, pullContext } from "../../../../TypingUtils";
import { EmptyRow } from "../../../MultiLine/Internals/EmptyRow";
import { CrossfitTableBodyMultilineRow } from "../CrossfitTableBodyRow/CrossfitTableBodyMultilineRow";
import { CrossfitTableBodyRow } from "../CrossfitTableBodyRow/CrossfitTableBodyRow";
import Sticky from "../HackedControls/Sticky";

import * as styles from "./CrossfitTableBody.styles";
import { CrossfitTableFooterRow } from "./CrossfitTableFooterRow";

export interface CrossfitBodyProps {
    children: React.ReactNode;
    colgroup: React.ReactElement<{ children?: React.ReactNode }>;
    left: number;
    emptyRowRequired: boolean;
    hasFilterInfo: boolean;
}

interface CrossfitTableBodyContext {
    getTableWidth: () => number;
    getHeaderHeight: () => number;
    getFooterHeight: () => number;
}

const FIRST_EMPTY_ROW_HEIGHT = 40;

export class CrossfitTableBody extends React.Component<CrossfitBodyProps> {
    public static contextTypes = {
        getTableWidth: PropTypes.func,
        getHeaderHeight: PropTypes.func,
        getFooterHeight: PropTypes.func,
    };

    public static defaultProps = { hasFilterInfo: false };

    private readonly stops: Array<null | HTMLElement> = [];
    private readonly tableBody = React.createRef<HTMLTableRowElement>();

    public componentDidMount(): void {
        if (this.tableBody.current != undefined) {
            // firefox < 61 version not support focusin
            events.addEventListener(this.tableBody.current, "focus", this.handleOnFocusIn, true);
        }
    }

    public componentWillUnmount(): void {
        if (this.tableBody.current) {
            events.removeEventListener(this.tableBody.current, "focus", this.handleOnFocusIn);
        }
    }

    public render(): JSX.Element {
        return (
            <tbody>
                <EmptyRow
                    isView={this.props.hasFilterInfo && this.props.emptyRowRequired}
                    height={FIRST_EMPTY_ROW_HEIGHT}
                />
                {this.renderRows()}
            </tbody>
        );
    }

    private renderRows(): Array<React.ReactNode | React.ReactNode[]> {
        const { children, colgroup } = this.props;
        const columns = React.Children.count(colgroup.props.children);

        return React.Children.map(children ?? [], (child, index: number): React.ReactNode | React.ReactNode[] => {
            if (isChildOfType(child, CrossfitTableBodyRow)) {
                return child;
            }
            if (isChildOfType(child, CrossfitTableBodyMultilineRow)) {
                if (child.props.isSticky) {
                    return [
                        <tr key="sticky-row-1" ref={el => (this.stops[index] = el)}>
                            <td style={{ display: "none" }} />
                        </tr>,
                        <tr key="sticky-row-2" ref={this.tableBody}>
                            <td colSpan={columns}>{React.cloneElement(child, { colgroup: colgroup })}</td>
                        </tr>,
                    ];
                }
                return [
                    <tr key="sticky-row-single">
                        <td colSpan={columns}>{React.cloneElement(child, { colgroup: colgroup })}</td>
                    </tr>,
                ];
            }
            if (isChildOfType(child, CrossfitTableFooterRow)) {
                if (child.props.isSticky) {
                    return this.renderStickyFooter(columns, child, index - 1);
                }
                return (
                    <tr>
                        <td colSpan={columns}>{child}</td>
                    </tr>
                );
            }

            return undefined;
        });
    }

    private getContext(): CrossfitTableBodyContext {
        return pullContext<CrossfitTableBodyContext>(this);
    }

    private readonly handleOnFocusIn = (_: Event): void => {
        const activeElement = document.activeElement;
        const documentElement = document.documentElement;
        const nodeName = activeElement && activeElement.nodeName ? activeElement.nodeName.toLowerCase() : undefined;
        const isValidNodeName = nodeName === "input";

        if (isValidNodeName && activeElement != undefined && documentElement != undefined) {
            const elementRect = activeElement.getBoundingClientRect();
            const headerHeight = this.getContext().getHeaderHeight();
            const footerHeight = this.getContext().getFooterHeight();
            const screenHeight = documentElement.clientHeight;

            const isElementIntersectHeader = elementRect.top <= headerHeight;
            const isElementIntersectFooter = elementRect.bottom >= screenHeight - footerHeight;

            if (isElementIntersectHeader) {
                smoothScrollBy(elementRect.top - headerHeight - 20);
            } else if (isElementIntersectFooter) {
                smoothScrollBy(elementRect.bottom - screenHeight + footerHeight + 20);
            }
        }
    };

    private renderStickyFooter(columns: number, footer: JSX.Element, index: number): JSX.Element {
        const { left } = this.props;

        return (
            <tr>
                <td colSpan={columns} style={{ height: `${this.getContext().getFooterHeight()}px` }}>
                    <Sticky getStop={() => this.stops[index]} offset={getScrollWidth() + 60} side="bottom">
                        {(fixed: boolean, leftOffset: number, stoppedTop: boolean) => {
                            const isShowBorder = leftOffset <= left + PAGE_PADDING;
                            const width = this.getContext().getTableWidth() - 2;

                            return (
                                <div
                                    css={themed(
                                        styles.footer,
                                        isShowBorder && !stoppedTop && styles.footerBorder,
                                        fixed && !stoppedTop && styles.fixedFooter,
                                        (!fixed || stoppedTop) && styles.absoluteFooter
                                    )}
                                    style={
                                        fixed
                                            ? { width: width, left: `${stoppedTop ? 0 : left}px` }
                                            : { bottom: "0px", left: `${PAGE_PADDING + 1}px` }
                                    }>
                                    <div css={themed(styles.footerContent, !fixed && styles.absoluteFooterContent)}>
                                        {footer}
                                    </div>
                                </div>
                            );
                        }}
                    </Sticky>
                </td>
            </tr>
        );
    }
}
